import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { fetchProfile, PROFILES_API_URL } from "../utils/profileApi.js";
async function setProfilePreference({
  key,
  preferredType,
  signature,
  signer,
  timestamp
}) {
  const response = await fetch(`${PROFILES_API_URL}/preference`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      key,
      preferredType,
      signature,
      signer,
      timestamp
    })
  });
  if (!response.ok) {
    throw new Error(`Failed to set preference: ${response.statusText}`);
  }
  return response.json();
}
async function setDisplayName({
  key,
  displayName,
  signature,
  signer,
  timestamp
}) {
  const response = await fetch(`${PROFILES_API_URL}/display-name`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      key,
      displayName,
      signature,
      signer,
      timestamp
    })
  });
  if (!response.ok) {
    throw new Error(`Failed to set display name: ${response.statusText}`);
  }
  return response.json();
}
export function useProfile(t0, options) {
  const $ = _c(14);
  const {
    address,
    name,
    b3GlobalId,
    fresh: t1
  } = t0;
  const fresh = t1 === undefined ? false : t1;
  const t2 = address || name || b3GlobalId;
  let t3;
  if ($[0] !== fresh || $[1] !== t2) {
    t3 = ["profile", t2, fresh];
    $[0] = fresh;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== address || $[4] !== b3GlobalId || $[5] !== fresh || $[6] !== name) {
    t4 = () => fetchProfile({
      address,
      name,
      b3GlobalId,
      fresh
    });
    $[3] = address;
    $[4] = b3GlobalId;
    $[5] = fresh;
    $[6] = name;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = (options?.enabled ?? true) && (!!address || !!name || !!b3GlobalId);
  const t6 = options?.refetchInterval;
  const t7 = options?.staleTime ?? 300000;
  let t8;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t8 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      refetchInterval: t6,
      staleTime: t7
    };
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return useQuery(t8);
}
export function useProfilePreference() {
  const setPreference = async (key, preferredType, signerAddress, signMessage) => {
    const timestamp = Math.floor(Date.now() / 1000);
    const message = `SetProfilePreference:${key}:${preferredType}:${timestamp}`;
    try {
      const signature = await signMessage(message);
      return setProfilePreference({
        key,
        preferredType,
        signature,
        signer: signerAddress,
        timestamp
      });
    } catch (error) {
      throw new Error(`Failed to set profile preference: ${error}`);
    }
  };
  return {
    setPreference
  };
}
export function useDisplayName() {
  const setName = async (key, displayName, signerAddress, signMessage) => {
    const timestamp = Math.floor(Date.now() / 1000);
    const message = `SetDisplayName:${key}:${displayName}:${timestamp}`;
    try {
      const signature = await signMessage(message);
      return setDisplayName({
        key,
        displayName,
        signature,
        signer: signerAddress,
        timestamp
      });
    } catch (error) {
      throw new Error(`Failed to set display name: ${error}`);
    }
  };
  return {
    setName
  };
}
export function useProfileSettings() {
  const $ = _c(7);
  const {
    setPreference
  } = useProfilePreference();
  const {
    setName
  } = useDisplayName();
  let t0;
  if ($[0] !== setName || $[1] !== setPreference) {
    t0 = async (key, signerAddress, signMessage, settings) => {
      const results = {};
      ;
      try {
        if (settings.preferredType) {
          results.preference = await setPreference(key, settings.preferredType, signerAddress, signMessage);
        }
        if (settings.displayName) {
          results.displayName = await setName(key, settings.displayName, signerAddress, signMessage);
        }
        return results;
      } catch (t1) {
        const error = t1;
        throw new Error(`Failed to update profile settings: ${error}`);
      }
    };
    $[0] = setName;
    $[1] = setPreference;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const updateSettings = t0;
  let t1;
  if ($[3] !== setName || $[4] !== setPreference || $[5] !== updateSettings) {
    t1 = {
      setPreference,
      setDisplayName: setName,
      updateSettings
    };
    $[3] = setName;
    $[4] = setPreference;
    $[5] = updateSettings;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}
export default useProfile;